import React from 'react';
import { Grid, InputLabel, OutlinedInput, Select, MenuItem, Checkbox, FormControlLabel, FormGroup, Typography, Divider } from '@mui/material';
import { IIrpItemAddEditRequest } from '../../../../model/scp/irp/request/irp-item-add-edit-request';
import { ITableResponse } from '../../../../model/scp/purchase/response/tables-response';
import BrazilianNumberInput from '../../../../components/brazilian-number-input';

export interface IIrpItemFormProps {
    itemIrp: IIrpItemAddEditRequest;
    materialService: ITableResponse[];
    categoryItemPca: ITableResponse[];
    judgementCriteria: ITableResponse[];
    isRetification: boolean;
    handleOnChange: (e: any) => void;
    handleCurrencyChange: (value: string | undefined, name: string) => void;
}

export default function IrpItemForm({ props }: { props: IIrpItemFormProps }) {
    const {
        itemIrp,
        materialService,
        categoryItemPca,
        judgementCriteria,
        isRetification,
        handleOnChange,
        handleCurrencyChange
    } = props;

    return (
        <Grid container spacing={3}>
            {isRetification && (
                <>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            Este item já foi enviado ao PNCP. Apenas descrição, informação complementar, unidade de fornecimento
                            e valor unitário estimado podem ser retificados, mediante justificativa.
                        </Typography>
                    </Grid>
                </>
            )}

            {/* Seção: Classificação do Item */}
            <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, mt: isRetification ? 2 : 0, color: 'primary.main', fontWeight: 600 }}>
                    Classificação do Item
                </Typography>
                <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} lg={6} sm={12} xl={6}>
                <InputLabel>Tipo</InputLabel>
                <Select
                    autoComplete='off'
                    onChange={handleOnChange}
                    onBlur={handleOnChange}
                    fullWidth
                    disabled={isRetification}
                    error={!isRetification && itemIrp?.typeItem === undefined ? true : false}
                    value={itemIrp.typeItem ? itemIrp.typeItem : ''}
                    name='select-type-item'
                >
                    <MenuItem value={undefined}>
                        <em>Selecionar</em>
                    </MenuItem>
                    {materialService.map((element) => (
                        <MenuItem value={Number(element.id)} key={element.id}>{element.id} - {element.name}</MenuItem>
                    ))}
                </Select>
            </Grid>

            <Grid item xs={12} lg={6} sm={12} xl={6}>
                <InputLabel>Categoria</InputLabel>
                <Select
                    autoComplete='off'
                    onChange={handleOnChange}
                    onBlur={handleOnChange}
                    fullWidth
                    disabled={isRetification}
                    error={!isRetification && itemIrp?.categoryItemId === undefined ? true : false}
                    value={itemIrp.categoryItemId ? itemIrp.categoryItemId : ''}
                    name='select-category-item'
                >
                    <MenuItem value={undefined}>
                        <em>Selecionar</em>
                    </MenuItem>
                    {categoryItemPca.map((element) => (
                        <MenuItem value={Number(element.id)} key={element.id}>{element.id} - {element.name}</MenuItem>
                    ))}
                </Select>
            </Grid>

            <Grid item xs={12} lg={6} sm={12} xl={6}>
                <InputLabel>Critério de Julgamento</InputLabel>
                <Select
                    autoComplete='off'
                    onChange={handleOnChange}
                    onBlur={handleOnChange}
                    fullWidth
                    disabled={isRetification}
                    error={!isRetification && itemIrp?.criterionJudgmentId === undefined ? true : false}
                    value={itemIrp.criterionJudgmentId ? itemIrp.criterionJudgmentId : ''}
                    name='select-criterion-judgment'
                >
                    <MenuItem value={undefined}>
                        <em>Selecionar</em>
                    </MenuItem>
                    {judgementCriteria.map((element) => (
                        <MenuItem value={Number(element.id)} key={element.id}>{element.id} - {element.name}</MenuItem>
                    ))}
                </Select>
            </Grid>

            {/* Seção: Valores e Medidas */}
            <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                    Valores e Medidas
                </Typography>
                <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} lg={6} sm={6} xl={6}>
                <InputLabel>Unidade de Fornecimento</InputLabel>
                <OutlinedInput
                    autoComplete='off'
                    onChange={handleOnChange}
                    onBlur={handleOnChange}
                    inputProps={{ maxLength: 30 }}
                    error={itemIrp?.unitSupply === undefined || itemIrp.unitSupply.length === 0 ? true : false}
                    value={itemIrp?.unitSupply || ''}
                    name='input-unit-supply'
                    fullWidth
                />
            </Grid>

            <Grid item xs={12} lg={6} sm={6} xl={6}>
                <InputLabel>Valor Unitário Estimado</InputLabel>
                <BrazilianNumberInput
                    value={itemIrp?.unitValueEstimated}
                    onChange={(value) => handleCurrencyChange(value.toString(), 'input-unit-value-estimated')}
                    decimals={4}
                    prefix="R$"
                    placeholder="0,0000"
                    error={itemIrp?.unitValueEstimated === undefined || itemIrp?.unitValueEstimated === 0}
                    fullWidth
                />
            </Grid>

            {/* Seção: Descrição */}
            <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                    Descrição
                </Typography>
                <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
                <InputLabel>Descrição</InputLabel>
                <OutlinedInput
                    autoComplete='off'
                    multiline
                    onChange={handleOnChange}
                    onBlur={handleOnChange}
                    rows={5}
                    inputProps={{ maxLength: 2048 }}
                    error={itemIrp?.description === undefined || itemIrp?.description.length === 0 ? true : false}
                    value={itemIrp?.description || ''}
                    name='input-description'
                    fullWidth
                />
                <Typography variant="caption" color={(itemIrp?.description?.length ?? 0) >= 2048 ? 'error' : 'text.secondary'} sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                    {itemIrp?.description?.length || 0}/2048
                </Typography>
            </Grid>

            {isRetification && (
                <Grid item xs={12}>
                    <InputLabel>Informação Complementar</InputLabel>
                    <OutlinedInput
                        autoComplete='off'
                        multiline
                        onChange={handleOnChange}
                        onBlur={handleOnChange}
                        rows={3}
                        value={itemIrp?.complementaryInformation || ''}
                        name='input-complementary-information-item'
                        fullWidth
                    />
                </Grid>
            )}

            {!isRetification && (
                <>
                    {/* Seção: Opções Adicionais */}
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                            Opções Adicionais
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                        <FormGroup>
                            <FormControlLabel
                                labelPlacement="start"
                                control={
                                    <Checkbox
                                        name="check-applicability-margin-preference"
                                        onChange={handleOnChange}
                                        onBlur={handleOnChange}
                                        checked={itemIrp?.applicabilityMarginPreference || false}
                                    />
                                }
                                label="Aplicabilidade de Margem de Preferência"
                            />
                            <FormControlLabel
                                labelPlacement="start"
                                control={
                                    <Checkbox
                                        name="check-requirement-national-content"
                                        onChange={handleOnChange}
                                        onBlur={handleOnChange}
                                        checked={itemIrp?.requirementNationalContent || false}
                                    />
                                }
                                label="Exigência de Conteúdo Nacional"
                            />
                            <FormControlLabel
                                labelPlacement="start"
                                control={
                                    <Checkbox
                                        name="check-ind-confidential-value"
                                        onChange={handleOnChange}
                                        onBlur={handleOnChange}
                                        checked={itemIrp?.indConfidentialValue || false}
                                    />
                                }
                                label="Valor Sigiloso"
                            />
                        </FormGroup>
                    </Grid>
                </>
            )}

            {isRetification && (
                <>
                    {/* Seção: Justificativa */}
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                            Justificativa
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                        <InputLabel required>Justificativa da Retificação</InputLabel>
                        <OutlinedInput
                            autoComplete='off'
                            multiline
                            onChange={handleOnChange}
                            onBlur={handleOnChange}
                            rows={3}
                            error={itemIrp?.justification === undefined || itemIrp.justification.length === 0}
                            value={itemIrp?.justification || ''}
                            name='input-justification-item'
                            fullWidth
                            required
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
}
